import dynamic from "next/dynamic";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";

import { useState } from "react";

import truncate from "lodash/truncate";

import { faLinkedin } from "@fortawesome/free-brands-svg-icons/faLinkedin";
import { faTwitter } from "@fortawesome/free-brands-svg-icons/faTwitter";
import { faGlobe } from "@fortawesome/free-solid-svg-icons/faGlobe";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import { profileUrl } from "components/ProfilePageLayout";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { emberAppBaseUrl } from "utils/envVariables";

const NewPrivateMessageModal = dynamic(
  () => import("components/NewPrivateMessageModal"),
  { ssr: false }
);

type ProfilePageOverviewListProps = {};

const ProfilePageOverviewList = ({}: ProfilePageOverviewListProps) => {
  const router = useRouter();
  const slug = router.query.slug as string;

  const isCompany = router.route.startsWith("/companies/[slug]");

  const { account } = useAuthentication();

  const { data: profile } = useApi(profileUrl(slug, isCompany));

  const [showNewMessageModal, setShowNewMessageModal] = useState(false);

  if (!profile) return <></>;

  const {
    type,
    company,
    summary,
    areasOfExpertise,
    workExperience,
    education,
    isInsightProvider,
    title: profileTitle,
    name,
    companyName,
  } = profile;

  const {
    assetClassPrimary,
    geography,
    countryGeneralist,
    countryPrimary,
    countrySecondary,
    sectorGeneralist,
    sectorPrimary,
    sectorSecondary,
    contentVerticalGeneralist,
    contentVerticalPrimary,
    contentVerticalSecondary,
  } = areasOfExpertise || {};

  const linkedinUrl =
    type === "companies" ? profile.linkedIn : company?.linkedIn;
  const twitterUrl = type === "companies" ? profile.twitter : company?.twitter;
  const website = type === "companies" ? profile.website : company?.website;

  const haveExpertises =
    assetClassPrimary ||
    geography ||
    countryGeneralist ||
    countryPrimary ||
    countrySecondary ||
    sectorGeneralist ||
    sectorPrimary ||
    sectorSecondary ||
    contentVerticalGeneralist ||
    contentVerticalPrimary ||
    contentVerticalSecondary;

  const haveDetails =
    summary ||
    (isInsightProvider && haveExpertises) ||
    !!workExperience?.length ||
    !!education?.length ||
    linkedinUrl ||
    twitterUrl ||
    website;

  const title = `${name}${companyName ? ` - ${companyName}` : ""} - About`;
  const description = truncate(
    `${profileTitle ? `${profileTitle}. ` : ""}${summary || ""}`,
    {
      length: 150,
      separator: /[/\- ]/,
    }
  );

  const metaHeader = (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
    </Head>
  );

  if (!haveDetails) {
    if (account?.id === profile.id) {
      return (
        <>
          {metaHeader}
          <div className="px-[32px] py-[25px] flex flex-col space-y-[42px] bg-[#f7dd59] rounded-lg">
            <i>
              Your profile is incomplete.{" "}
              <a
                className="text-smart-teal"
                href={`${emberAppBaseUrl}/settings/profile`}
              >
                Fill in your details
              </a>{" "}
              for more meaningful engagements on the Smartkarma network.
            </i>
          </div>
        </>
      );
    }

    if (isCompany) {
      return (
        <>
          {metaHeader}
          <div className="px-[32px] py-[25px] flex flex-col space-y-[42px]">
            <i>Profile details have not been filled out.</i>
          </div>
        </>
      );
    }

    return (
      <>
        {metaHeader}
        <div className="px-[32px] py-[25px] flex flex-col space-y-[42px]">
          <i>
            Profile details have not been filled out. Send a private message to
            contact{" "}
            <a
              className="text-smart-teal cursor-pointer"
              onClick={() => setShowNewMessageModal(true)}
            >
              {profile.name}
            </a>{" "}
          </i>
        </div>
        {showNewMessageModal && (
          <NewPrivateMessageModal
            preselectRecipients={[profile]}
            onClose={() => setShowNewMessageModal(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      {metaHeader}
      <div className="px-[32px] py-[25px] flex flex-col space-y-[42px]">
        {summary && (
          <div className="">
            <h4 className="text-[18px] font-[900]">Summary</h4>
            <div>{summary}</div>
          </div>
        )}
        {isInsightProvider && haveExpertises && (
          <div className="">
            <h4 className="text-[18px] font-[900]">Areas of Expertise</h4>
            <div className="flex">
              <dl className="grid grid-cols-3 grid- gap-x-5">
                <div>Primary Asset Class:</div>
                <div className="col-span-2">{assetClassPrimary}</div>
                <div>Geography:</div>
                <div className="col-span-2">{geography}</div>
                <div>Countries:</div>
                <div className="col-span-2">
                  {countryGeneralist ? (
                    <i>(generalist)</i>
                  ) : (
                    [countryPrimary, countrySecondary]
                      .filter((c) => c)
                      .join(", ")
                  )}
                </div>
                <div>Sectors:</div>
                <div className="col-span-2">
                  {sectorGeneralist ? (
                    <i>(generalist)</i>
                  ) : (
                    [sectorPrimary, sectorSecondary].filter((c) => c).join(", ")
                  )}
                </div>
                <div>Content Verticals:</div>
                <div className="col-span-2">
                  {contentVerticalGeneralist ? (
                    <i>(generalist)</i>
                  ) : (
                    [contentVerticalPrimary, contentVerticalSecondary]
                      .filter((c) => c)
                      .join(", ")
                  )}
                </div>
              </dl>
            </div>
          </div>
        )}
        {!!workExperience?.length && (
          <div className="">
            <h4 className="text-[18px] font-[900]">Work Experience:</h4>
            <ul>
              {workExperience.map((e: string) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        )}
        {!!education?.length && (
          <div className="">
            <h4 className="text-[18px] font-[900]">Education:</h4>
            <ul>
              {education.map((e: string) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        )}
        {(linkedinUrl || twitterUrl || website) && (
          <div className="">
            <h4 className="text-[18px] font-[900]">Links</h4>
            <div className="flex flex-col space-y-2">
              {linkedinUrl && (
                <Link
                  href={linkedinUrl}
                  rel="nofollow"
                  target="_blank"
                  className="flex items-center group"
                >
                  <FontAwesomeIcon
                    icon={faLinkedin}
                    className="text-[21px] text-[#7b8895] group-hover:text-smart-teal mr-[5px]"
                  />
                  LinkedIn
                </Link>
              )}
              {twitterUrl && (
                <Link
                  href={`https://twitter.com/${twitterUrl}`}
                  rel="nofollow"
                  target="_blank"
                  className="flex items-center group"
                >
                  <FontAwesomeIcon
                    icon={faTwitter}
                    className="text-[21px] text-[#7b8895] group-hover:text-smart-teal mr-[5px]"
                  />
                  Twitter
                </Link>
              )}
              {website && (
                <Link
                  href={website}
                  rel="nofollow"
                  target="_blank"
                  className="flex items-center group"
                >
                  <FontAwesomeIcon
                    icon={faGlobe}
                    className="text-[21px] text-[#7b8895] group-hover:text-smart-teal mr-[5px]"
                  />
                  Website
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProfilePageOverviewList;
